import { Button, Card, Field, Input } from '@cabgo/ui';
import { type FormEvent, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../lib/auth';

export function LoginPage() {
  const { t } = useTranslation('auth');
  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [email, setEmail] = useState(import.meta.env.DEV ? 'admin@cabgo.local' : '');
  const [password, setPassword] = useState(import.meta.env.DEV ? 'admin1234' : '');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (import.meta.env.DEV && params.get('autologin') === '1') {
      setBusy(true);
      login('admin@cabgo.local', 'admin1234')
        .then(() => {
          const from = (location.state as { from?: { pathname: string } } | null)?.from?.pathname ?? '/';
          navigate(from, { replace: true });
        })
        .catch(() => {
          setError(t('invalidCredentials'));
          setBusy(false);
        });
    }
  }, [location, login, navigate, t]);

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await login(email, password);
      const from = (location.state as { from?: { pathname: string } } | null)?.from?.pathname ?? '/';
      navigate(from, { replace: true });
    } catch {
      setError(t('invalidCredentials'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="login">
      <Card className="login__card">
        <div className="login__brand">
          <img className="login__logo login__logo--customer" src="/cabgo-customer.png" alt="CabGo Customer" />
          <div>
            <div className="cg-h3">{t('loginTitle')}</div>
            <div className="cg-sm cg-muted">{t('loginSubtitle')}</div>
          </div>
        </div>
        <form onSubmit={onSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          <Field label={t('email')} required error={error ?? undefined}>
            {(id, invalid) => (
              <Input
                id={id}
                type="email"
                autoComplete="username"
                value={email}
                invalid={invalid}
                onChange={(e) => setEmail(e.target.value)}
                required
              />
            )}
          </Field>
          <Field label={t('password')} required>
            {(id) => (
              <Input
                id={id}
                type="password"
                autoComplete="current-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
              />
            )}
          </Field>
          <Button type="submit" disabled={busy} block>
            {busy ? t('loggingIn') : t('login')}
          </Button>
        </form>
      </Card>
    </div>
  );
}
